<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { ArrowLeft } from 'lucide-vue-next'
import { findResumeStage, formatDateBr, sortStagesBySequence } from '@ck/domain'
import { CkAlert, CkButton, CkCard, CkStepper } from '@ck/ui'
import { workOrdersApi } from '@ck/api-client'
import { useWorkOrderCover } from '../composables/useWorkOrderCover'

const props = defineProps<{ id: string }>()
const router = useRouter()

const workOrderId = computed(() => props.id)
const workOrderQuery = useQuery({ queryKey: ['work-order', props.id], queryFn: () => workOrdersApi.get(props.id) })
const workOrder = computed(() => workOrderQuery.data.value)
const { coverUrl } = useWorkOrderCover(workOrderId)

const sortedStages = computed(() => sortStagesBySequence(workOrder.value?.stages ?? []))
const resumeStage = computed(() => findResumeStage(sortedStages.value))
const resumeIndex = computed(() => resumeStage.value ? sortedStages.value.findIndex((stage) => stage.id === resumeStage.value?.id) : -1)
const progress = computed(() => resumeStage.value?.progress ?? (sortedStages.value.length ? 100 : 0))

function openChecklist(stageId: string) {
  const stage = sortedStages.value.find((candidate) => candidate.id === stageId)
  if (stage) router.push({ name: 'checklist', params: { id: props.id, stageId: stage.id } })
}
function openResumeChecklist() {
  if (resumeStage.value) openChecklist(resumeStage.value.id)
}

const entrada = computed(() => formatDateBr(workOrder.value?.createdAt))
const previsao = computed(() => workOrder.value?.expectedDelivery ? formatDateBr(workOrder.value.expectedDelivery) : 'A definir')
</script>

<template>
  <div class="app">
    <header class="page-header">
      <CkButton variant="ghost" class="back" aria-label="Voltar" @click="router.push({ name: 'home' })"><ArrowLeft :size="20" aria-hidden="true" /></CkButton>
      <div><div class="ck-eyebrow">{{ workOrder ? `OS ${workOrder.number}` : '—' }}</div><h1>{{ workOrder ? `${workOrder.vehicle.brand} ${workOrder.vehicle.model}` : 'Veículo' }}</h1></div>
    </header>

    <CkAlert v-if="workOrderQuery.isError.value" tone="error">Não foi possível carregar esta ordem de serviço.</CkAlert>
    <p v-else-if="workOrderQuery.isPending.value" class="status-message">Carregando…</p>
    <template v-else-if="workOrder">
      <div class="vehicle-layout">
        <aside class="rail" aria-label="Resumo da ordem de serviço">
          <img v-if="coverUrl" class="cover" :src="coverUrl" alt="Foto do veículo">
          <CkCard class="data-grid">
            <div><span class="ck-eyebrow">Cliente</span><strong>{{ workOrder.vehicle.customer.name }}</strong></div>
            <div><span class="ck-eyebrow">Cor</span><strong>{{ workOrder.vehicle.color ?? '—' }}</strong></div>
            <div><span class="ck-eyebrow">Placa</span><strong class="ck-code">{{ workOrder.vehicle.plate }}</strong></div>
            <div><span class="ck-eyebrow">Criação da OS</span><strong>{{ entrada }}</strong></div>
            <div><span class="ck-eyebrow">Previsão</span><strong>{{ previsao }}</strong></div>
            <div class="vin"><span class="ck-eyebrow">Chassi (VIN)</span><strong class="ck-code">{{ workOrder.vehicle.vin ?? '—' }}</strong></div>
          </CkCard>

          <CkCard v-if="resumeStage" class="resume-card">
            <span class="ck-eyebrow">Retomada segura</span>
            <strong>Etapa {{ String(resumeStage.sequence).padStart(2, '0') }} · {{ resumeStage.title }}</strong>
            <span class="resume-copy">Primeira etapa pendente pela sequência da API.</span>
            <CkButton class="resume-button" @click="openResumeChecklist">Retomar checklist</CkButton>
          </CkCard>
          <CkAlert v-else tone="success">Todas as etapas desta OS foram concluídas.</CkAlert>
        </aside>

        <main class="panel">
          <div class="section-head"><span class="ck-eyebrow">Etapas da restauração</span><span class="ck-code progress-pct">{{ progress }}%</span></div>
          <CkCard class="stage-card">
            <CkStepper
              :stages="sortedStages"
              :current-index="Math.max(0, resumeIndex)"
              :current-stage-id="resumeStage?.id ?? null"
              orientation="vertical"
              @select="openChecklist"
            />
            <p v-if="resumeStage" class="hint">Todas as etapas autorizadas podem ser abertas. A retomada sugere a menor sequência pendente.</p>
          </CkCard>
        </main>
      </div>
    </template>
    <p v-else class="status-message">Ordem de serviço não encontrada.</p>
  </div>
</template>

<style scoped>
.app{max-width:1280px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px 40px;background:var(--ck-cream-100)}
.page-header{display:flex;align-items:center;gap:12px;margin-bottom:18px}
.back{display:flex;align-items:center;justify-content:center;width:44px;min-width:44px;height:44px;padding:0;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:var(--ck-cream-50);color:var(--ck-maroon-700)}
.page-header h1{margin:2px 0 0;font-size:22px;line-height:1.1}
.status-message{padding:20px 0;color:var(--ck-text-muted);font-size:13px}
.vehicle-layout{display:grid;grid-template-columns:1fr;gap:20px}
.rail,.panel{min-width:0}
.cover{display:block;width:100%;height:180px;object-fit:cover;margin-bottom:16px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg)}
.data-grid{display:grid;grid-template-columns:1fr;gap:12px 14px;margin-bottom:16px}
.data-grid .vin{padding-top:9px;border-top:1px solid var(--ck-cream-200)}
.data-grid strong{display:block;margin-top:2px;font-size:13.5px;font-weight:600;overflow-wrap:anywhere}
.resume-card{display:flex;flex-direction:column;gap:6px;margin-bottom:16px}
.resume-card>strong{font-size:15px;line-height:1.3}
.resume-copy{font-size:12px;color:var(--ck-text-muted)}
.resume-button{align-self:flex-start;margin-top:6px}
.section-head{display:flex;justify-content:space-between;align-items:center;margin:0 0 10px}
.progress-pct{color:var(--ck-orange-500);font-weight:700}
.stage-card{min-height:100%}
.hint{margin:14px 0 0;font-size:12px;color:var(--ck-orange-500);font-weight:600;line-height:1.45}

@media (min-width:540px) {
  .data-grid{grid-template-columns:1fr 1fr}
  .data-grid .vin{grid-column:1/3}
}

@media (min-width:768px) {
  .app{padding:32px 32px 52px}
  .vehicle-layout{grid-template-columns:minmax(270px,340px) minmax(0,1fr);gap:28px;align-items:start}
  .rail{position:sticky;top:24px}
  .stage-card{padding:28px}
}

@media (min-width:1200px) {
  .app{padding:40px 48px 64px}
  .vehicle-layout{grid-template-columns:320px minmax(0,1fr);gap:40px}
  .panel{max-width:860px}
}

@media (max-width:375px) {
  .app{padding-left:14px;padding-right:14px}
  .page-header h1{font-size:20px}
}
</style>
